import React, { useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { GalleryPhoto } from './GalleryPhoto';
import galleryPhotos from '../data/solutionGalleryPhotos.json';
import './SolutionGallery.css';

export const SolutionGallery = ({ solution }) => {
  const images = useMemo(() => [
    ...galleryPhotos[solution.id].map(({ alt }, index) => ({
      src: `/images/solutions-generated/${solution.id}-${index + 1}.webp`,
      alt, generated: true,
    })),
    ...(solution.keepPreviousImage === false ? [] : [{ src: solution.image, alt: solution.alt, generated: false }]),
  ], [solution]);
  const [viewportRef, api] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selected, setSelected] = useState(0);
  const viewportId = `gallery-viewport-${solution.id}`;

  useEffect(() => {
    if (!api) return;
    const update = () => setSelected(api.selectedScrollSnap());
    update();
    api.on('select', update);
    api.on('reInit', update);
    return () => { api.off('select', update); api.off('reInit', update); };
  }, [api]);

  const jump = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const previous = () => api?.scrollPrev(jump());
  const next = () => api?.scrollNext(jump());
  const onKeyDown = event => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const actions = { ArrowLeft: previous, ArrowRight: next, Home: () => api?.scrollTo(0, jump()), End: () => api?.scrollTo(images.length - 1, jump()) };
    if (actions[event.key]) { event.preventDefault(); event.stopPropagation(); actions[event.key](); }
  };

  return (
    <div className="solution-image solution-gallery" role="region" aria-roledescription="carrossel"
      aria-label={`Galeria de ${solution.title.toLocaleLowerCase('pt-BR')}`} tabIndex={0}
      aria-keyshortcuts="ArrowLeft ArrowRight Home End" onKeyDown={onKeyDown} data-testid={`gallery-${solution.id}`}
      data-current-slide={selected + 1} data-total-slides={images.length}>
      <div className="gallery-viewport" ref={viewportRef} id={viewportId} data-testid={`gallery-viewport-${solution.id}`}>
        <div className="gallery-track">
          {images.map((image, index) => (
            <div className="gallery-slide" key={image.src} role="group" aria-roledescription="slide"
              aria-label={`Foto ${index + 1} de ${images.length}`} aria-hidden={index !== selected}
              data-testid={`gallery-slide-${solution.id}-${index + 1}`}>
              <GalleryPhoto image={image} category={solution.id} index={index} />
            </div>
          ))}
        </div>
      </div>
      <span className="solution-number" aria-hidden="true">{solution.number}</span>
      <Button type="button" variant="outline" size="icon" className="gallery-arrow gallery-previous" onClick={previous} disabled={!api}
        aria-label={`Foto anterior — ${solution.title}`} aria-controls={viewportId} data-testid={`gallery-prev-${solution.id}`}><ChevronLeft size={18} aria-hidden="true" /></Button>
      <Button type="button" variant="outline" size="icon" className="gallery-arrow gallery-next" onClick={next} disabled={!api}
        aria-label={`Próxima foto — ${solution.title}`} aria-controls={viewportId} data-testid={`gallery-next-${solution.id}`}><ChevronRight size={18} aria-hidden="true" /></Button>
      <div className="gallery-bottom-bar">
        <div className="gallery-indicators" role="group" aria-label={`Escolher foto de ${solution.title.toLocaleLowerCase('pt-BR')}`}>
          {images.map((image, index) => <button type="button" key={image.src} className="gallery-indicator"
            onClick={() => api?.scrollTo(index, jump())} aria-label={`Ver foto ${index + 1} de ${images.length}: ${image.alt}`}
            aria-current={index === selected ? 'true' : undefined} aria-controls={viewportId}
            data-testid={`gallery-dot-${solution.id}-${index + 1}`}><span /></button>)}
        </div>
        <span className="gallery-counter" role="status" aria-live="polite" aria-atomic="true" data-testid={`gallery-counter-${solution.id}`}>
          <span className="sr-only">{solution.title}, foto </span>{selected + 1}<span aria-hidden="true"> / </span><span className="sr-only"> de </span>{images.length}
        </span>
      </div>
    </div>
  );
};